import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SubjectIcon from "@mui/icons-material/Subject";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import { Brightness1 as CalendarDot } from "@mui/icons-material";
import RemoveIcon from "@mui/icons-material/Remove";
import PropTypes from "prop-types";
import Modal from "../../Modal";
import { defaultEventDurations } from "../../../constants";
import "../../../scss/geosuggest.scss";
import moment from "moment-timezone";
import Geosuggest from "react-geosuggest";
import { getAddressByPlaceId } from "../../../services/googleMapsService";
import { useTranslation } from "react-i18next";
import { replaceTimeZone, toFullDate } from "../../../utils/datetime";
import { isIframeAdmin } from "../../../utils/iframe";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import {
  LocalizationProvider,
  MobileDatePicker,
  MobileTimePicker
} from "@mui/x-date-pickers";

const useStyles = makeStyles(theme => ({
  eventTitle: {
    ...theme.components.MuiTypography.styleOverrides.h4
  },
  section: {
    display: "flex",
    marginTop: theme.spacing(3)
  },
  dateField: {
    ...theme.components.MuiTypography.styleOverrides.subtitle1,
    color: theme.palette.text.secondary,
    cursor: "pointer",
    maxWidth: 96,
    width: 96, // Fix for IE
    "& > *:only-child > input": {
      padding: 0
    }
  },
  calendarDot: {
    marginRight: theme.spacing(2)
  }
}));

const hasError = (errors, key) => {
  return !!errors && errors.hasOwnProperty(key);
};

const errorMessages = (errors, key) => {
  if (!hasError(errors, key)) {
    return "";
  }

  let capitalized = errors[key].map(
    str => str.charAt(0).toUpperCase() + str.slice(1)
  );

  return `${capitalized.join(". ")}.`;
};

const DateAndTimeSelection = ({
  startTime,
  endTime,
  handleStartDateChange,
  handleEndDateChange,
  allDay,
  errors
}) => {
  const classes = useStyles();

  const onUpdateAllDayEventDate = event => {
    let start = allDay
      ? moment(event)
          .startOf("day")
          .format("MMM D, YYYY h:mm A")
      : event;
    handleStartDateChange(start);
  };

  React.useEffect(() => {
    if (allDay) {
      let endOfDay = moment(startTime)
        .endOf("day")
        .format("MMM D, YYYY h:mm A");
      handleEndDateChange(endOfDay);
    }
  }, [startTime]);

  return (
    <React.Fragment>
      <Box
        display="flex"
        alignItems="center"
        flexDirection={{ xs: "column", sm: "row" }}
      >
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <Box display="flex" alignItems="center">
            <Box display={{ xs: "flex", sm: "none" }} minWidth={44}>
              <Typography variant="body2" color="textSecondary">
                Start:
              </Typography>
            </Box>
            <MobileDatePicker
              className={classes.dateField}
              InputProps={{
                disableUnderline: true
              }}
              renderInput={params => (
                <TextField variant="standard" {...params} />
              )}
              format="MMM dd, yyyy"
              value={startTime}
              minDate={new Date()}
              onChange={e => onUpdateAllDayEventDate(e)}
            />
            <MobileTimePicker
              className={classes.dateField}
              InputProps={{
                disableUnderline: true
              }}
              disabled={allDay}
              style={{ display: allDay ? "none" : "block" }}
              value={startTime}
              onChange={e => handleStartDateChange(e)}
              renderInput={params => (
                <TextField
                  variant="standard"
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    style: { textAlign: "center" }
                  }}
                />
              )}
            />
          </Box>
          <Box pt={0.5} display={{ xs: "none", sm: "flex" }}>
            <RemoveIcon color="action" fontSize="small" />
          </Box>
          <Box display="flex" alignItems="center">
            <Box display={{ xs: "flex", sm: "none" }} minWidth={44}>
              <Typography variant="body2" color="textSecondary">
                End:
              </Typography>
            </Box>
            <Box
              display="flex"
              flexDirection={{ xs: "row-reverse", sm: "row" }}
            >
              <MobileTimePicker
                className={classes.dateField}
                InputProps={{
                  disableUnderline: true
                }}
                disabled={allDay}
                style={{ display: allDay ? "none" : "block" }}
                format="hh:mma"
                value={endTime}
                onChange={event => handleEndDateChange(event)}
                renderInput={params => (
                  <TextField
                    variant="standard"
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      style: { textAlign: "center" }
                    }}
                  />
                )}
              />
              <MobileDatePicker
                className={classes.dateField}
                InputProps={{
                  disableUnderline: true
                }}
                format="MMM dd, yyyy"
                minDate={startTime}
                disabled={allDay}
                value={endTime}
                onChange={event => handleEndDateChange(event)}
                renderInput={params => (
                  <TextField variant="standard" {...params} />
                )}
              />
            </Box>
          </Box>
        </LocalizationProvider>
      </Box>
      <Box pt={0.5} display={{ xs: "none", sm: "flex" }}>
        <Typography variant="body2" color="error">
          {errorMessages(errors, "startTime")}
          {errorMessages(errors, "endTime")}
        </Typography>
      </Box>
    </React.Fragment>
  );
};
const CalendarMenu = ({
  calendars,
  currentCalendar,
  onCalendarChange,
  isAdmin
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const filteredCalendars = () => {
    if (isAdmin) return calendars;
    return calendars.filter(calendar => calendar.name === "My Events");
  };

  return (
    <React.Fragment>
      <Button
        className={classes.buttons}
        onClick={ev => {
          setAnchorEl(ev.currentTarget);
        }}
      >
        <CalendarDot
          className={classes.calendarDot}
          fontSize="small"
          style={{ color: currentCalendar?.color }}
        />
        <Typography variant="subtitle1" color="textSecondary">
          {currentCalendar?.name}
        </Typography>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
      >
        {filteredCalendars().map((option, index) => (
          <MenuItem
            key={"calendar-" + index}
            onClick={() => {
              onCalendarChange(option);
              setAnchorEl(null);
            }}
          >
            <CalendarDot
              className={classes.calendarDot}
              fontSize="small"
              style={{ color: option.color }}
            />
            <Typography variant="subtitle1" color="textSecondary">
              {option.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </React.Fragment>
  );
};

const getInitialEndDate = defaultEventDuration => {
  if (defaultEventDuration) {
    if (defaultEventDuration === defaultEventDurations.halfHour) {
      return moment()
        .add(30, "minutes")
        .toDate();
    } else if (defaultEventDuration === defaultEventDurations.oneHour) {
      return moment()
        .add(1, "hours")
        .toDate();
    } else if (defaultEventDuration === defaultEventDurations.allDay) {
      return moment()
        .add(1, "days")
        .startOf("day")
        .toDate();
    }
  } else {
    return new Date();
  }
};

const getInitialStartDate = defaultEventDuration => {
  if (
    defaultEventDuration &&
    defaultEventDuration === defaultEventDurations.allDay
  ) {
    return moment()
      .startOf("day")
      .toDate();
  } else {
    return new Date();
  }
};

const getCalendar = (calendars, event) =>
  calendars.filter(
    calendar => calendar.id === event.extendedProps.calendarId
  )[0];

const getEventEndDate = event => {
  return event.allDay && !event.end
    ? moment(event.start)
        .endOf("day")
        .format("MMM D, YYYY h:mm A")
    : event.allDay && event.end
    ? moment(replaceTimeZone(event.end))
        .endOf("day")
        .format("MMM D, YYYY h:mm A")
    : event.end;
};

const getEventStartDate = event => {
  return event.allDay
    ? moment(event.start)
        .startOf("day")
        .format("MMM D, YYYY h:mm A")
    : event.start;
};

export default function CreateEvent(props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    calendars,
    open,
    event,
    user,
    onOpenChange,
    onSubmit,
    onDelete,
    defaultEventDuration,
    errors
  } = props;

  const defaultState = {
    name: event ? event.title : "",
    startTime: event
      ? getEventStartDate(event)
      : getInitialStartDate(defaultEventDuration),
    endTime: event
      ? getEventEndDate(event)
      : getInitialEndDate(defaultEventDuration),
    lat: null,
    lng: null,
    description: event ? event.description : "",
    calendar: event ? getCalendar(calendars, event) : null,
    allDay: event
      ? event.allDay
      : defaultEventDuration === defaultEventDurations.allDay
  };
  let eventExtendedData = event ? event.extendedProps : {};
  const [eventData, setEventData] = React.useState({
    ...defaultState,
    ...eventExtendedData
  });

  const [locationAddress, setLocationAddress] = React.useState(null);

  const isAdmin = React.useMemo(() => {
    return (
      user.isAdmin ||
      isIframeAdmin(user, ["Policy Admin", "System Admin", "Validate"])
    );
  }, [user]);

  React.useEffect(() => {
    if (calendars) {
      setEventData({
        ...eventData,
        calendar: isAdmin
          ? calendars[0]
          : calendars.filter(calendar => calendar.name === "My Events")[0]
      });
    }
  }, [calendars]);

  const handleEventDataChange = field => event => {
    setEventData({
      ...eventData,
      [field]: event && event.target ? event.target.value : event
    });
  };

  const handleAllDayCheckedChange = checked => {
    setEventData({
      ...eventData,
      allDay: checked
    });
  };

  const handleSuggestSelect = suggest => {
    if (!suggest) {
      setEventData({
        ...eventData,
        placeId: null,
        lat: null,
        lng: null,
        placeLabel: null
      });
    } else {
      setEventData({
        ...eventData,
        placeId: suggest.placeId,
        lat: suggest.location.lat,
        lng: suggest.location.lng,
        placeLabel: suggest.label
      });
    }
  };

  const permitUpdate = () => {
    if (!event || isAdmin) return true;
    return eventData.calendarName === "My Events";
  };

  const permitDelete = () => {
    return event && permitUpdate();
  };

  React.useEffect(() => {
    if (eventData.allDay && eventData.calendar) {
      setEventData({
        ...eventData,
        startTime: moment(eventData.startTime)
          .startOf("day")
          .local()
          .format("MMM D, YYYY h:mm A"),
        endTime:
          eventData.endTime instanceof Date
            ? toFullDate(eventData.endTime)
            : moment(replaceTimeZone(eventData.startTime))
                .endOf("day")
                .format("MMM D, YYYY h:mm A"),
        startDate: getInitialStartDate(defaultEventDurations.allDay),
        endDate: getInitialEndDate(defaultEventDurations.allDay)
      });
    } else if (!eventData.allDay && eventData.calendar) {
      setEventData({
        ...eventData,
        startTime: moment(eventData.startTime).toDate(),
        endTime: moment(eventData.endTime).toDate()
      });
    } else if (eventData.allDay) {
      setEventData({
        ...eventData,
        startDate: getInitialStartDate(defaultEventDurations.allDay),
        endDate: getInitialEndDate(defaultEventDurations.allDay)
      });
    }
  }, [eventData.allDay]);

  if (eventData.placeId && !locationAddress) {
    getAddressByPlaceId(eventData.placeId, address => {
      setLocationAddress(address);
    });
  }

  return (
    <Modal
      maxWidth="sm"
      fullWidth
      scrollable
      dialogTitle={
        <TextField
          placeholder="Add Title"
          variant="standard"
          margin="normal"
          fullWidth
          error={hasError(errors, "name")}
          helperText={errorMessages(errors, "name")}
          InputProps={{ className: classes.eventTitle }}
          onChange={handleEventDataChange("name")}
          value={eventData.name}
          hiddenLabel
        />
      }
      dialogContent={
        <Box display="flex" flexDirection="column">
          <Box className={classes.section}>
            <Box mr={2.5}>
              <ScheduleIcon color="action" />
            </Box>
            <Box display="flex" flexDirection="column" pl={2}>
              <DateAndTimeSelection
                startTime={eventData.startTime}
                endTime={eventData.endTime}
                handleStartDateChange={handleEventDataChange("startTime")}
                handleEndDateChange={handleEventDataChange("endTime")}
                allDay={eventData.allDay}
                errors={errors}
              />
              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      value="allDay"
                      color="primary"
                      checked={eventData.allDay}
                      onChange={event =>
                        handleAllDayCheckedChange(event.target.checked)
                      }
                    />
                  }
                  label="All Day"
                />
                <Button className={classes.buttons}>
                  <Typography variant="body2" color="textSecondary">
                    Does Not Repeat
                  </Typography>
                </Button>
              </Box>
            </Box>
          </Box>

          <Box className={classes.section} alignItems="center">
            <Box mr={2.5}>
              <LocationOnOutlinedIcon color="action" />
            </Box>
            <Geosuggest
              placeholder="Search Locations"
              onSuggestSelect={handleSuggestSelect}
              initialValue={eventData.placeLabel}
            />
          </Box>

          <Box className={classes.section}>
            <Box pt={2.75} mr={2.5}>
              <SubjectIcon color="action" />
            </Box>
            <TextField
              placeholder="Add Description"
              rows="3"
              variant="filled"
              fullWidth
              multiline
              InputProps={{
                disableUnderline: true
              }}
              hiddenLabel
              value={eventData.description}
              onChange={handleEventDataChange("description")}
            />
          </Box>

          {calendars && calendars.length > 0 && (
            <Box className={classes.section}>
              <Box mr={2.5} pt={0.5}>
                <EventRoundedIcon color="action" />
              </Box>
              <CalendarMenu
                currentCalendar={eventData.calendar}
                isAdmin={isAdmin}
                calendars={calendars}
                onCalendarChange={handleEventDataChange("calendar")}
              />
            </Box>
          )}
        </Box>
      }
      dialogActions={
        <>
          <Button color="primary" onClick={() => onOpenChange(false)}>
            {t("cancelBtn")}
          </Button>
          {permitUpdate() && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => onSubmit(eventData)}
            >
              {event ? t("updateBtn") : t("createBtn")}
            </Button>
          )}
          {permitDelete() && (
            <Button variant="contained" onClick={() => onDelete(event)}>
              {t("deleteBtn")}
            </Button>
          )}
        </>
      }
      open={open}
      onClose={() => onOpenChange(false)}
      showCloseIcon
      editableTitle
    />
  );
}

CreateEvent.propTypes = {
  user: PropTypes.object,
  /**
   * True if the create event modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the create event modal is open or closed
   */
  onOpenChange: PropTypes.func,
  calendars: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The name of the calendar
       */
      name: PropTypes.string,
      /**
       * The calendar's associated color
       */
      color: PropTypes.string
    })
  ),
  /**
   * Called when the user presses the create button to create a new event.
   */
  onSubmit: PropTypes.func,
  onDelete: PropTypes.func
};
